import {
  ChangeEvent,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { Checkbox, Dropdown } from 'components/Input'
import { DropdownOption } from 'components/Input/Dropdown'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'
import ROUTES from 'app/routes'
import { useAppSelector } from 'app/hooks'
import {
  sitePKSsSsidSelector,
  sitePSKsFetchDataSelector,
} from 'features/networkAi/selectors'
import {
  CheckboxField,
  PasswordField,
  TextField,
  TextArea,
  ToggleField,
} from 'components/Fields'
//import { DropdownOption } from 'components/Input/Dropdown'
import RadioField from 'components/Fields/RadioField'
import {
  StyledWLANForm,
  WLANFormBlock,
  WLANFormHeading,
  WLANFormSeparator,
  WLANFormSSIDInputs,
  WLANFormPSKsInputs,
  WLANFormPSKsPassword,
  WLANFormPSKVlanTitle,
  WLANFormSSIDName,
  WLANFormSubTitle,
  WLANFormBlockSubgroup,
  WLANFormGuestTimeInputs,
  WLANFormGuestTimeAmountInputs,
  WLANFormGuestTimeAmountDropdown,
  WLANBonjourWrapperInputs,
  WLANBonjourInputs,
  WLANFormSpacer,
} from './styles'
import { fieldsMessages } from 'components/Fields/messages'
import { createWLANMessages } from './messages'
import {
  createWLANThunk,
  fetchSiteWlansThunk,
  updateWLANThunk,
  createSitePSKsThunk,
  fetchSitePSKsThunk,
  updateSitePSKsThunk,
  deleteSitePSKsThunk,
} from 'features/networkAi/slice'
import {
  CreateWLANBody,
  UpdateWLANBody,
  WLANBonjourServices,
  WLANBonjourServicesItem,
  SiteWlan,
  Services,
  SitePSKs,
  UpdateSitePSKs,
} from 'features/networkAi/types'
import { CommonButton } from 'components/Button'
import { useAppDispatch } from 'app/hooks'
import { defaultRateSet, noLegacyRateSet } from './consts'
import SuccessModal from 'components/Modal/SuccessModal'
import { DEFAULT_WLAN_VALUES } from 'features/networkAi/consts'
import { SpinnerCentered } from 'components/Loader'

interface createWLANFormValues {
  id: string
  enabled: boolean
  ssid: string
  hide_ssid: boolean
  SSID_password: string // part of .auth
  multi_psk_only: boolean // part of .auth
  savant_passPhrase_remote: string
  savant_passPhrase_homeowner: string
  savant_passPhrase_custom: string
  savant_passPhrase_remote_id: string
  savant_passPhrase_homeowner_id: string
  savant_passPhrase_custom_id: string
  savant_passPhrase_remote_enabled: boolean
  savant_passPhrase_homeowner_enabled: boolean
  savant_passPhrase_custom_enabled: boolean
  savant_vlanid_remote: string
  savant_vlanid_homeowner: string
  savant_vlanid_custom: string
  portal_enabled: boolean // part of .portal
  portal_passphrase_enabled: boolean // part of .portal
  portal_password: string // part of .portal
  portal_email_enabled: boolean // part of .portal
  portal_sms_enabled: boolean // part of .portal
  portal_google_enabled: boolean // part of .portal
  portal_facebook_enabled: boolean // part of .portal
  portal_amazon_enabled: boolean // part of .portal
  portal_microsoft_enabled: boolean // part of .portal
  portal_expire: number // part of .portal
  savant_expire_range: number // part of .portal
  rateSet: boolean
  bands: string // ["24", "5"]
  bands_24: boolean
  bands_5: boolean
  bands_6: boolean
  roam_mode: string
  vlan_id: ''
  disable_11ax: boolean // wi-fi 6
  bonjour_enabled: boolean
  bonjour_services_items: string
  bonjour_additional_vlan_ids: string
}

const GUEST_RANGE_OPTIONS = [
  { value: 1, label: 'Minutes' },
  { value: 60, label: 'Hours' },
  { value: 1440, label: 'Days' },
  { value: 525600, label: 'Years' },
]

const GuestRangeOptions = GUEST_RANGE_OPTIONS.map((option) => ({
  label: option.label,
  value: option.value,
}))

const newFormValues: createWLANFormValues = {
  id: '',
  enabled: true,
  ssid: '',
  SSID_password: '',
  hide_ssid: false,
  multi_psk_only: false,
  savant_passPhrase_remote: '',
  savant_passPhrase_homeowner: '',
  savant_passPhrase_custom: '',
  savant_passPhrase_remote_enabled: false,
  savant_passPhrase_homeowner_enabled: false,
  savant_passPhrase_custom_enabled: false,
  savant_passPhrase_remote_id: '',
  savant_passPhrase_homeowner_id: '',
  savant_passPhrase_custom_id: '',
  savant_vlanid_remote: '',
  savant_vlanid_homeowner: '',
  savant_vlanid_custom: '',
  portal_enabled: false,
  portal_passphrase_enabled: false,
  portal_password: '',
  portal_email_enabled: false,
  portal_sms_enabled: false,
  portal_google_enabled: false,
  portal_facebook_enabled: false,
  portal_amazon_enabled: false,
  portal_microsoft_enabled: false,
  portal_expire: 1,
  savant_expire_range: 1400,
  bands: '',
  bands_24: true,
  bands_5: true,
  bands_6: false,
  rateSet: false,
  roam_mode: 'NONE',
  vlan_id: '',
  disable_11ax: false,
  bonjour_enabled: true,
  bonjour_services_items: '',
  bonjour_additional_vlan_ids: '',
}

const mapNewFormValuesToRequest = (
  values: createWLANFormValues,
  enabledBonjour: boolean,
  bonjourServicesItems: Array<string>,
  guestDuration: number
): CreateWLANBody => {
  return Object.keys(values).reduce((acc, key) => {
    let newValues: CreateWLANBody = { ...acc }
    switch (key) {
      case 'multi_psk_only':
        newValues.auth.multi_psk_only = values[key]
        if (newValues.auth.multi_psk_only) {
          newValues.auth.type = 'psk'
          newValues.auth.private_wlan = true
        } else {
          newValues.auth.private_wlan = false
        }
        break
      case 'SSID_password':
        newValues.auth.type = 'psk'
        newValues.auth.psk = values[key]
        break
      case 'portal_enabled':
        newValues.portal.enabled = values[key]
        if (newValues.portal.enabled && newValues.auth.psk === '') {
          newValues.auth.type = 'open'
        }
        break
      case 'portal_passphrase_enabled':
        newValues.portal.passphrase_enabled = values[key]
        break
      case 'portal_password':
        newValues.portal.password = values[key]
        break
      case 'portal_email_enabled':
        newValues.portal.email_enabled = values[key]
        break
      case 'portal_sms_enabled':
        newValues.portal.sms_enabled = values[key]
        break
      case 'portal_google_enabled':
        newValues.portal.google_enabled = values[key]
        break
      case 'portal_facebook_enabled':
        newValues.portal.facebook_enabled = values[key]
        break
      case 'portal_amazon_enabled':
        newValues.portal.amazon_enabled = values[key]
        break
      case 'portal_microsoft_enabled':
        newValues.portal.microsoft_enabled = values[key]
        break
      case 'portal_expire':
        newValues.portal.expire = values[key] * guestDuration
        break
      case 'bands':
        newValues.bands = []
        break
      case 'bands_24':
        if (values[key]) {
          newValues.bands.push('24')
        }
        break
      case 'bands_5':
        if (values[key]) {
          newValues.bands.push('5')
        }
        break
      case 'bands_6':
        if (values[key]) {
          newValues.auth.type = 'psk'
          newValues.bands.push('6')
          newValues.auth.pairwise = ['wpa2-ccmp', 'wpa3']
        }
        break
      case 'disable_11ax':
        newValues.disable_11ax = !values[key]
        break
      case 'rateSet':
        newValues.rateset = values[key] ? noLegacyRateSet : defaultRateSet
        break
      case 'vlan_id':
        if (values[key] !== '') {
          newValues.vlan_enabled = true
          if (values[key].indexOf(',') !== -1) {
            newValues.vlan_ids = [values[key]]
            newValues.vlan_id = null
            newValues.vlan_pooling = true
          } else {
            newValues.vlan_id = values[key]
            newValues.vlan_ids = []
            newValues.vlan_pooling = false
          }
        }
        break
      case 'bonjour_enabled':
        newValues.bonjour.enabled = enabledBonjour
        break
      case 'bonjour_services_items':
        newValues.bonjour.services = {}
        const sl: WLANBonjourServices = {}
        const si: WLANBonjourServicesItem = { scope: 'same_site' }
        bonjourServicesItems.forEach(function (value) {
          sl[value] = si
          if (value === 'airplay') {
            sl['raop'] = si
            sl['companion-link'] = si
          }
          if (value === 'airprint') {
            sl['printer'] = si
            sl['ipp'] = si
            sl['ipps'] = si
            sl['pdl-datastream'] = si
            sl['ipp'] = si
            sl['ipp'] = si
          }
          if (value === 'homekit') {
            sl['hap'] = si
          }
          if (value === 'scanner') {
            sl['uscans'] = si
          }
        })
        newValues.bonjour.services = sl
        break
      case 'bonjour_additional_vlan_ids':
        newValues.bonjour.additional_vlan_ids = values[key]
        break
      case 'savant_passPhrase_remote':
      case 'savant_passPhrase_homeowner':
      case 'savant_passPhrase_custom':
      case 'savant_passPhrase_remote_enabled':
      case 'savant_passPhrase_homeowner_enabled':
      case 'savant_passPhrase_custom_enabled':
      case 'savant_passPhrase_remote_id':
      case 'savant_passPhrase_homeowner_id':
      case 'savant_passPhrase_custom_id':
      case 'savant_vlanid_remote':
      case 'savant_vlanid_homeowner':
      case 'savant_vlanid_custom':
      case 'savant_expire_range':
        break
      default:
        newValues = {
          ...newValues,
          [key]: values[key as keyof createWLANFormValues],
        }
    }
    return newValues
  }, DEFAULT_WLAN_VALUES as CreateWLANBody)
}

const mapNewPSKsToRequest = (
  siteId: string,
  passphrase: string,
  name: string,
  ssid: string,
  vlanid: string
) => {
  const sitePSKsValues: SitePSKs = {
    usage: 'multi',
    notify_on_create_or_edit: false,
    id: '',
    name: name,
    for_site: true,
    last_used: null,
    site_id: siteId,
    ssid: ssid,
    passphrase: passphrase,
    expire_time: null,
    portal_id: null,
    notify_expiry: false,
  }

  if (vlanid !== '' && !isNaN(parseInt(vlanid))) {
    sitePSKsValues.vlan_id = parseInt(vlanid)
  }

  return sitePSKsValues
}

const mapUpdateFormValuesToRequest = (
  values: createWLANFormValues,
  enabledBonjour: boolean,
  bonjourServicesItems: Array<string>,
  guestDuration: number
): UpdateWLANBody => {
  return Object.keys(values).reduce(
    (acc, key) => {
      let newValues: UpdateWLANBody = { ...acc }
      switch (key) {
        case 'multi_psk_only':
          newValues.auth.multi_psk_only = values[key]
          if (newValues.auth.multi_psk_only) {
            newValues.auth.type = 'psk'
            newValues.auth.private_wlan = true
          } else {
            newValues.auth.private_wlan = false
          }
          break
        case 'SSID_password':
          newValues.auth.psk = values[key]
          break
        case 'portal_enabled':
          newValues.portal.enabled = values[key]
          if (newValues.portal.enabled && newValues.auth.psk === '') {
            newValues.auth.type = 'open'
          }
          break
        case 'portal_passphrase_enabled':
          newValues.portal.passphrase_enabled = values[key]
          break
        case 'portal_password':
          newValues.portal.password = values[key]
          break
        case 'portal_email_enabled':
          newValues.portal.email_enabled = values[key]
          break
        case 'portal_sms_enabled':
          newValues.portal.sms_enabled = values[key]
          break
        case 'portal_google_enabled':
          newValues.portal.google_enabled = values[key]
          break
        case 'portal_facebook_enabled':
          newValues.portal.facebook_enabled = values[key]
          break
        case 'portal_amazon_enabled':
          newValues.portal.amazon_enabled = values[key]
          break
        case 'portal_microsoft_enabled':
          newValues.portal.microsoft_enabled = values[key]
          break
        case 'portal_expire':
          newValues.portal.expire = values[key] * guestDuration
          break
        case 'bands':
          break
        case 'bands_24':
          if (values[key]) {
            newValues.bands.push('24')
          }
          break
        case 'bands_5':
          if (values[key]) {
            newValues.bands.push('5')
          }
          break
        case 'bands_6':
          if (values[key]) {
            newValues.bands.push('6')
            newValues.auth.pairwise = ['wpa2-ccmp', 'wpa3']
          }
          break
        case 'disable_11ax':
          newValues.disable_11ax = !values[key]
          break
        case 'rateSet':
          newValues.rateset = values[key] ? noLegacyRateSet : defaultRateSet
          break
        case 'vlan_id':
          if (values[key] !== '') {
            newValues.vlan_enabled = true
            if (values[key].indexOf(',') !== -1) {
              newValues.vlan_ids = [values[key]]
              newValues.vlan_id = null
              newValues.vlan_pooling = true
            } else {
              newValues.vlan_id = values[key]
              newValues.vlan_ids = []
              newValues.vlan_pooling = false
            }
          }
          break
        case 'bonjour_enabled':
          newValues.bonjour.enabled = enabledBonjour
          newValues.bonjour.services = {}
          break
        case 'bonjour_services_items':
          newValues.bonjour.services = {}
          const sl: WLANBonjourServices = {}
          const si: WLANBonjourServicesItem = { scope: 'same_site' }
          bonjourServicesItems.forEach(function (value) {
            sl[value] = si
            if (value === 'airplay') {
              sl['raop'] = si
              sl['companion-link'] = si
            }
            if (value === 'airprint') {
              sl['printer'] = si
              sl['ipp'] = si
              sl['ipps'] = si
              sl['pdl-datastream'] = si
              sl['ipp'] = si
              sl['ipp'] = si
            }
            if (value === 'homekit') {
              sl['hap'] = si
            }
            if (value === 'scanner') {
              sl['uscans'] = si
            }
          })
          newValues.bonjour.services = sl
          break
        case 'bonjour_additional_vlan_ids':
          newValues.bonjour.additional_vlan_ids = values[key]
          break
        case 'savant_passPhrase_remote':
        case 'savant_passPhrase_homeowner':
        case 'savant_passPhrase_custom':
        case 'savant_passPhrase_remote_enabled':
        case 'savant_passPhrase_homeowner_enabled':
        case 'savant_passPhrase_custom_enabled':
        case 'savant_passPhrase_remote_id':
        case 'savant_passPhrase_homeowner_id':
        case 'savant_passPhrase_custom_id':
        case 'savant_vlanid_remote':
        case 'savant_vlanid_homeowner':
        case 'savant_vlanid_custom':
        case 'savant_expire_range':
          break
        default:
          newValues = {
            ...newValues,
            [key]: values[key as keyof createWLANFormValues],
          }
      }
      return newValues
    },
    {
      ssid: '',
      enabled: true,
      hide_ssid: false,
      bands: [],
      disable_11ax: true,
      vlan_enabled: false,
      vlan_id: null,
      portal: {
        enabled: false,
        auth: 'none',
        privacy: false,
        password: '',
        expire: 1440,
        passphrase_enabled: false,
        email_enabled: false,
        sms_enabled: false,
        google_enabled: false,
        facebook_enabled: false,
        amazon_enabled: false,
        microsoft_enabled: false,
      },
      auth: {
        type: 'psk',
        enable_mac_auth: false,
        private_wlan: false,
        keys: [null, null, null, null],
        key_idx: 1,
        multi_psk_only: false,
        psk: '',
      },
      rateset: {
        5: {
          template: 'compatible',
          min_rssi: 0,
        },
        6: {
          template: 'compatible',
          min_rssi: 0,
        },
        24: {
          template: 'compatible',
          min_rssi: 0,
        },
      },
      vlan_ids: [],
      vlan_pooling: false,
      bonjour: {
        enabled: false,
        services: {},
        additional_vlan_ids: '',
      },
      roam_mode: 'NONE',
      id: '',
    } as UpdateWLANBody
  )
}

interface CreateWLANFormProps {
  onSubmit: VoidFunction
  siteId: string
  rootDomainId: string
  jobId: string
  homeId: string
  wlan: SiteWlan
  wlanName: string
  defaultExpanded?: boolean
  wizardMode?: boolean
}

/* 1111eslint-disable */

function removeItem<T>(arr: Array<T>, value: T): Array<T> {
  const index = arr.indexOf(value)
  if (index > -1) {
    arr.splice(index, 1)
  }
  return arr
}

function generateInitialValues(wlan: SiteWlan, sitePSKs: SitePSKs[]) {
  if (wlan.id !== undefined) {
    const wlanValues: createWLANFormValues = {
      id: wlan.id,
      enabled: wlan.enabled,
      ssid: wlan.ssid,
      SSID_password: wlan.auth.psk,
      hide_ssid: wlan.hide_ssid,
      multi_psk_only: wlan.auth.multi_psk_only,
      savant_passPhrase_remote: '',
      savant_passPhrase_homeowner: '',
      savant_passPhrase_custom: '',
      savant_passPhrase_remote_enabled: false,
      savant_passPhrase_homeowner_enabled: false,
      savant_passPhrase_custom_enabled: false,
      savant_passPhrase_remote_id: '',
      savant_passPhrase_homeowner_id: '',
      savant_passPhrase_custom_id: '',
      savant_vlanid_remote: '',
      savant_vlanid_homeowner: '',
      savant_vlanid_custom: '',
      portal_enabled: wlan.portal.enabled,
      portal_passphrase_enabled: wlan.portal.passphrase_enabled,
      portal_password: wlan.portal.password,
      portal_email_enabled: wlan.portal.email_enabled,
      portal_sms_enabled: wlan.portal.sms_enabled,
      portal_google_enabled: wlan.portal.google_enabled,
      portal_facebook_enabled: wlan.portal.facebook_enabled,
      portal_amazon_enabled: wlan.portal.amazon_enabled,
      portal_microsoft_enabled: wlan.portal.microsoft_enabled,
      portal_expire: wlan.portal.expire,
      savant_expire_range: 1440,
      bands: wlan.bands.toString().replace(',', '_'),
      bands_24: wlan.bands.toString().indexOf('24') !== -1,
      bands_5: wlan.bands.toString().indexOf('5') !== -1,
      bands_6: wlan.bands.toString().indexOf('6') !== -1,
      roam_mode: wlan.roam_mode,
      rateSet: false,
      vlan_id: wlan.vlan_id || '',
      disable_11ax: !wlan.disable_11ax,
      bonjour_enabled: wlan.bonjour.enabled,
      bonjour_services_items: '',
      bonjour_additional_vlan_ids: wlan.bonjour.additional_vlan_ids,
    }

    if (wlan.vlan_pooling) {
      wlanValues.vlan_id = wlan.vlan_ids
    }

    sitePSKs.forEach(function (value) {
      switch (value.name) {
        case 'Custom':
          wlanValues.savant_passPhrase_custom_enabled = true
          wlanValues.savant_passPhrase_custom = value.passphrase
          wlanValues.savant_passPhrase_custom_id = value.id
          if (value.vlan_id) {
            wlanValues.savant_vlanid_custom = value.vlan_id?.toString()
          }
          break
        case 'Savant Remote':
          wlanValues.savant_passPhrase_remote_enabled = true
          wlanValues.savant_passPhrase_remote = value.passphrase
          wlanValues.savant_passPhrase_remote_id = value.id
          if (value.vlan_id) {
            wlanValues.savant_vlanid_remote = value.vlan_id?.toString()
          }
          break
        case 'Homeowner':
          wlanValues.savant_passPhrase_homeowner_enabled = true
          wlanValues.savant_passPhrase_homeowner = value.passphrase
          wlanValues.savant_passPhrase_homeowner_id = value.id
          if (value.vlan_id) {
            wlanValues.savant_vlanid_homeowner = value.vlan_id?.toString()
          }
          break
        default:
          console.log(value.name)
      }
    })

    if (wlan.portal.expire % 525600 === 0) {
      wlanValues.portal_expire = wlan.portal.expire / 525600
    } else if (wlan.portal.expire % 1440 === 0) {
      wlanValues.portal_expire = wlan.portal.expire / 1440
    } else if (wlan.portal.expire % 60 === 0) {
      wlanValues.portal_expire = wlan.portal.expire / 60
    } else {
      wlanValues.portal_expire = wlan.portal.expire
    }

    if (wlan.rateset[24].template === 'no-legacy') {
      wlanValues.rateSet = true
    }
    if (wlan.bonjour.services !== undefined) {
      wlanValues.bonjour_services_items = JSON.stringify(wlan.bonjour.services)
    }

    return wlanValues
  } else {
    return newFormValues
  }
}

function CreateWLANForm({
  onSubmit,
  wlan,
  siteId,
  rootDomainId,
  jobId,
  homeId,
  wlanName,
  defaultExpanded,
  wizardMode,
}: CreateWLANFormProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const isLoading = useAppSelector(sitePSKsFetchDataSelector.isFetching)

  useEffect(() => {
    wlan.ssid && dispatch(fetchSitePSKsThunk(siteId))
  }, [dispatch, siteId, wlan])

  const sitePSKs = useAppSelector(sitePKSsSsidSelector(wlan.ssid))
  const sitePSKsSafe: SitePSKs[] = useMemo(
    () => (Array.isArray(sitePSKs) ? sitePSKs : [{} as SitePSKs]),
    [sitePSKs]
  )

  //const [enabledGuestPassphrase, setEnabledGuestPassphrase] = useState(false)
  const [enabledBonjour, setEnabledBonjour] = useState(false)
  const [enabledBonjourSelect, setEnabledBonjourSelect] = useState(false)
  const [enabledBonjourAirdrop, setEnabledBonjourAirdrop] = useState(false)
  const [enabledBonjourAirplay, setEnabledBonjourAirplay] = useState(false)
  const [enabledBonjourAirprint, setEnabledBonjourAirprint] = useState(false)
  const [enabledBonjourAmazon, setEnabledBonjourAmazon] = useState(false)
  const [enabledBonjourHomekit, setEnabledBonjourHomekit] = useState(false)
  const [enabledBonjourGoogleCast, setEnabledBonjourGoogleCast] =
    useState(false)
  const [enabledBonjourNFS, setEnabledBonjourNFS] = useState(false)
  const [enabledBonjourRoku, setEnabledBonjourRoku] = useState(false)
  const [enabledBonjourScanner, setEnabledBonjourScanner] = useState(false)
  const [enabledBonjourSpotify, setEnabledBonjourSpotify] = useState(false)
  const [enabledBonjourSleep, setEnabledBonjourSleep] = useState(false)
  const [bonjourServicesItems, setBonjourServicesItems] = useState<
    Array<string>
  >([])
  const [isSuccess, setIsSuccess] = useState(false)
  const [guestDuration, setGuestDuration] = useState(1440)

  const formRef = useRef<FormikProps<createWLANFormValues> | null>(null)

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        ssid: Yup.string()
          .trim()
          .max(32, formatMessage(createWLANMessages.ssidMax))
          .required(formatMessage(fieldsMessages.requiredField)),
        SSID_password: Yup.string().when(['portal_enabled', 'multi_psk_only'], {
          is: false,
          then: Yup.string()
            .trim()
            .min(8, formatMessage(createWLANMessages.ssidPassMin))
            .max(64, formatMessage(createWLANMessages.ssidPassMax))
            .required(formatMessage(fieldsMessages.requiredField)),
        }),
        savant_passPhrase_remote: Yup.string().when(
          ['savant_passPhrase_remote_enabled'],
          {
            is: true,
            then: Yup.string()
              .trim()
              .min(8, formatMessage(createWLANMessages.pskPassMin))
              .max(64, formatMessage(createWLANMessages.pskPassMax))
              .required(
                formatMessage(createWLANMessages.requiredFieldPassphrase)
              ),
          }
        ),
        savant_passPhrase_homeowner: Yup.string().when(
          ['savant_passPhrase_homeowner_enabled'],
          {
            is: true,
            then: Yup.string()
              .trim()
              .min(8, formatMessage(createWLANMessages.pskPassMin))
              .max(64, formatMessage(createWLANMessages.pskPassMax))
              .required(
                formatMessage(createWLANMessages.requiredFieldPassphrase)
              ),
          }
        ),
        savant_passPhrase_custom: Yup.string().when(
          ['savant_passPhrase_custom_enabled'],
          {
            is: true,
            then: Yup.string()
              .trim()
              .min(8, formatMessage(createWLANMessages.pskPassMin))
              .max(64, formatMessage(createWLANMessages.pskPassMax))
              .required(
                formatMessage(createWLANMessages.requiredFieldPassphrase)
              ),
          }
        ),
        portal_password: Yup.string().when(['portal_passphrase_enabled'], {
          is: true,
          then: Yup.string()
            .trim()
            .min(8, formatMessage(createWLANMessages.pskPassMin))
            .max(64, formatMessage(createWLANMessages.pskPassMax))
            .required(
              formatMessage(createWLANMessages.requiredFieldPassphrase)
            ),
        }),
        portal_expire: Yup.number()
          .min(1, 'Please enter a number between 1 and 1000')
          .max(1000, 'Please enter a number between 1 and 1000')
          .required('Please enter a number between 1 and 1000'),
      }),
    [formatMessage]
  )

  const guestValueDuration = useMemo(
    () =>
      GuestRangeOptions.find((option) => option.value === guestDuration) || {
        label: `${guestDuration} MHz`,
        value: guestDuration,
      },
    [guestDuration]
  )

  const handleSubmit = async (values: createWLANFormValues) => {
    if (wlan.id === undefined) {
      const newWLANBody = mapNewFormValuesToRequest(
        values,
        enabledBonjour,
        bonjourServicesItems,
        guestDuration
      )
      await dispatch(createWLANThunk(siteId, newWLANBody))

      if (values.multi_psk_only) {
        if (values.savant_passPhrase_remote_enabled) {
          const newPSKsBody = mapNewPSKsToRequest(
            siteId,
            values.savant_passPhrase_remote,
            'Savant Remote',
            values.ssid,
            values.savant_vlanid_remote
          )
          await dispatch(createSitePSKsThunk(siteId, newPSKsBody))
        }

        if (values.savant_passPhrase_homeowner_enabled) {
          const newPSKsBody = mapNewPSKsToRequest(
            siteId,
            values.savant_passPhrase_homeowner,
            'Homeowner',
            values.ssid,
            values.savant_vlanid_homeowner
          )
          await dispatch(createSitePSKsThunk(siteId, newPSKsBody))
        }

        if (values.savant_passPhrase_custom_enabled) {
          const newPSKsBody = mapNewPSKsToRequest(
            siteId,
            values.savant_passPhrase_custom,
            'Custom',
            values.ssid,
            values.savant_vlanid_custom
          )
          await dispatch(createSitePSKsThunk(siteId, newPSKsBody))
        }
      }
    } else {
      const updateWLANBody = mapUpdateFormValuesToRequest(
        values,
        enabledBonjour,
        bonjourServicesItems,
        guestDuration
      )
      await dispatch(updateWLANThunk(siteId, updateWLANBody))

      if (values.multi_psk_only) {
        //Clear Deleted PSKs
        if (
          values.savant_passPhrase_remote_id !== '' &&
          !values.savant_passPhrase_remote_enabled
        ) {
          await dispatch(
            deleteSitePSKsThunk(siteId, values.savant_passPhrase_remote_id)
          )
        }
        if (
          values.savant_passPhrase_homeowner_id !== '' &&
          !values.savant_passPhrase_homeowner_enabled
        ) {
          await dispatch(
            deleteSitePSKsThunk(siteId, values.savant_passPhrase_homeowner_id)
          )
        }
        if (
          values.savant_passPhrase_custom_id !== '' &&
          !values.savant_passPhrase_custom_enabled
        ) {
          await dispatch(
            deleteSitePSKsThunk(siteId, values.savant_passPhrase_custom_id)
          )
        }

        //Set Enabled PSKs
        if (values.savant_passPhrase_remote_enabled) {
          if (values.savant_passPhrase_remote_id !== '') {
            const siteUpdatePSKsValues: UpdateSitePSKs = {
              id: values.savant_passPhrase_remote_id,
              name: 'Savant Remote',
              passphrase: values.savant_passPhrase_remote,
              ssid: values.ssid,
            }

            if (
              values.savant_vlanid_remote !== '' &&
              !isNaN(parseInt(values.savant_vlanid_remote))
            ) {
              siteUpdatePSKsValues.vlan_id = parseInt(
                values.savant_vlanid_remote
              )
            }

            await dispatch(
              updateSitePSKsThunk(
                siteId,
                values.savant_passPhrase_remote_id,
                siteUpdatePSKsValues
              )
            )
          } else {
            const newPSKsBody = mapNewPSKsToRequest(
              siteId,
              values.savant_passPhrase_remote,
              'Savant Remote',
              values.ssid,
              values.savant_vlanid_remote
            )
            await dispatch(createSitePSKsThunk(siteId, newPSKsBody))
          }
        }

        if (values.savant_passPhrase_homeowner_enabled) {
          if (values.savant_passPhrase_homeowner_id !== '') {
            const siteUpdatePSKsValues: UpdateSitePSKs = {
              id: values.savant_passPhrase_homeowner_id,
              name: 'Homeowner',
              passphrase: values.savant_passPhrase_homeowner,
              ssid: values.ssid,
            }

            if (
              values.savant_vlanid_homeowner !== '' &&
              !isNaN(parseInt(values.savant_vlanid_homeowner))
            ) {
              siteUpdatePSKsValues.vlan_id = parseInt(
                values.savant_vlanid_homeowner
              )
            }

            await dispatch(
              updateSitePSKsThunk(
                siteId,
                values.savant_passPhrase_homeowner_id,
                siteUpdatePSKsValues
              )
            )
          } else {
            const newPSKsBody = mapNewPSKsToRequest(
              siteId,
              values.savant_passPhrase_homeowner,
              'Homeowner',
              values.ssid,
              values.savant_vlanid_homeowner
            )
            await dispatch(createSitePSKsThunk(siteId, newPSKsBody))
          }
        }

        if (values.savant_passPhrase_custom_enabled) {
          if (values.savant_passPhrase_custom_id !== '') {
            const siteUpdatePSKsValues: UpdateSitePSKs = {
              id: values.savant_passPhrase_custom_id,
              name: 'Custom',
              passphrase: values.savant_passPhrase_custom,
              ssid: values.ssid,
            }

            if (
              values.savant_vlanid_custom !== '' &&
              !isNaN(parseInt(values.savant_vlanid_custom))
            ) {
              siteUpdatePSKsValues.vlan_id = parseInt(
                values.savant_vlanid_custom
              )
            }

            await dispatch(
              updateSitePSKsThunk(
                siteId,
                values.savant_passPhrase_custom_id,
                siteUpdatePSKsValues
              )
            )
          } else {
            const newPSKsBody = mapNewPSKsToRequest(
              siteId,
              values.savant_passPhrase_custom,
              'Custom',
              values.ssid,
              values.savant_vlanid_custom
            )
            await dispatch(createSitePSKsThunk(siteId, newPSKsBody))
          }
        }
      }
    }
    formRef.current?.resetForm()
    await setIsSuccess(true)
  }

  /*
  const setVlan = (e: ChangeEvent<any>) => { 
    console.log(e.target.value)
  }
*/

  //Bonjour
  const handleBonjourSwitch = useCallback((key: any, checked: any) => {
    switch (key) {
      case 'airdrop':
        setEnabledBonjourAirdrop(checked)
        break
      case 'airplay':
        setEnabledBonjourAirplay(checked)
        break
      case 'airprint':
        setEnabledBonjourAirprint(checked)
        break
      case 'amzn-wplay':
        setEnabledBonjourAmazon(checked)
        break
      case 'homekit':
        setEnabledBonjourHomekit(checked)
        break
      case 'googlecast':
        setEnabledBonjourGoogleCast(checked)
        break
      case 'nfs':
        setEnabledBonjourNFS(checked)
        break
      case 'rsp._tcp.local':
        setEnabledBonjourRoku(checked)
        break
      case 'scanner':
        setEnabledBonjourScanner(checked)
        break
      case 'spotify-connect':
        setEnabledBonjourSpotify(checked)
        break
      case 'sleep-proxy':
        setEnabledBonjourSleep(checked)
        break
      default:
        console.log(checked)
    }
  }, [])

  const populateBonjourServices = useCallback(
    (wlanBonjourServices: Services) => {
      let bji = []
      for (const [k] of Object.entries(wlanBonjourServices)) {
        switch (k) {
          case 'airdrop':
          case 'airplay':
          case 'airprint':
          case 'amzn-wplay':
          case 'homekit':
          case 'googlecast':
          case 'nfs':
          case 'rsp._tcp.local':
          case 'scanner':
          case 'spotify-connect':
          case 'sleep-proxy':
            bji.push(k)
            handleBonjourSwitch(k, true)
            break
          default:
            console.log(k)
        }
      }
      setBonjourServicesItems(bji)
    },
    [handleBonjourSwitch, setBonjourServicesItems]
  )

  const removeBonjourServices = (serviceName: string) => {
    removeItem(bonjourServicesItems, serviceName)
  }

  const addBonjourServices = (serviceName: string) => {
    bonjourServicesItems.push(serviceName)
  }

  const handleBonjourChange = (e: ChangeEvent<any>) => {
    if (e.target.checked) {
      addBonjourServices(e.target.value)
    } else {
      removeBonjourServices(e.target.value)
    }
    handleBonjourSwitch(e.target.value, e.target.checked)
  }

  useEffect(() => {
    if (wlan.id !== undefined) {
      if (wlan.portal.expire % 525600 === 0) {
        setGuestDuration(525600)
      } else if (wlan.portal.expire % 1440 === 0) {
        setGuestDuration(1440)
      } else if (wlan.portal.expire % 60 === 0) {
        setGuestDuration(60)
      } else {
        setGuestDuration(1)
      }

      if (wlan.bonjour.enabled === true) {
        setEnabledBonjour(true)
        setEnabledBonjourSelect(false)
        if (wlan.bonjour.services !== undefined) {
          populateBonjourServices(wlan.bonjour.services)
        }
      }
    }
  }, [
    wlan,
    setEnabledBonjour,
    populateBonjourServices,
    setEnabledBonjourSelect,
  ])

  return (
    <div id={wlanName}>
      {isLoading ? (
        <SpinnerCentered mt="200px" />
      ) : (
        <>
          <SuccessModal
            isOpen={isSuccess}
            onConfirm={async () => {
              await dispatch(fetchSiteWlansThunk(siteId))
              await dispatch(fetchSitePSKsThunk(siteId))

              if (wizardMode === false) {
                const pathname = generatePath(
                  ROUTES.networkAi.dashboard.networkWLANs,
                  {
                    rootDomainId,
                    jobId,
                    homeId,
                  }
                )
                history.push(pathname)
              } else {
                /*
                const pathname = generatePath(ROUTES.networkAi.wizard.createWLANs, {
                  rootDomainId,
                  jobId,
                  homeId,
                })
                history.push(pathname)
                */
              }
              setIsSuccess(false)
            }}
            title={`WLAN ${formRef.current?.values.ssid} saved`}
            confirmBtnTitle="Close"
          />
          <WLANFormSeparator />
          <Formik
            key={wlanName}
            onSubmit={handleSubmit}
            initialValues={generateInitialValues(wlan, sitePSKsSafe)}
            validationSchema={validationSchema}
            innerRef={formRef}
          >
            {(formikHelpers) => (
              <StyledWLANForm>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <WLANFormHeading>{wlanName}</WLANFormHeading>
                  <ToggleField
                    name="enabled"
                    label="Enabled"
                    inputId={`${wlanName}_enabled`}
                  />
                </div>
                <WLANFormBlock>
                  <WLANFormSSIDInputs>
                    <WLANFormSSIDName>
                      <TextField
                        name="ssid"
                        placeholder="SSID"
                        showLabel
                        inputId={`${wlanName}_ssid`}
                        autoComplete="new-password"
                      />
                    </WLANFormSSIDName>
                    {!formikHelpers.values.multi_psk_only && (
                      <PasswordField
                        name="SSID_password"
                        placeholder="SSID Password (WPA2)"
                        showLabel
                        inputId={`${wlanName}_password`}
                        autoComplete="new-password"
                      />
                    )}
                  </WLANFormSSIDInputs>
                  <div>
                    <CheckboxField name="hide_ssid" labelText="Hide SSID" />
                  </div>
                </WLANFormBlock>
                <WLANFormSeparator />
                <WLANFormBlock>
                  <CheckboxField
                    name="multi_psk_only"
                    labelText="SSID with WPA-2/PSK with Multiple Pass-Phrases"
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#A5A5A5',
                      marginTop: '5px',
                    }}
                  >
                    Devices logged in with different passwords will not see each
                    other on the LAN
                  </div>
                </WLANFormBlock>
                {formikHelpers.values.multi_psk_only && (
                  <>
                    <WLANFormBlock>
                      <CheckboxField
                        name="savant_passPhrase_remote_enabled"
                        labelText="Passphrase for Savant Remotes"
                      />
                      {formikHelpers.values
                        .savant_passPhrase_remote_enabled && (
                        <WLANFormPSKsInputs>
                          <WLANFormPSKsPassword>
                            <PasswordField
                              name="savant_passPhrase_remote"
                              placeholder="Pass-Phrase"
                              inputId={`${wlanName}_remote_password`}
                              autoComplete="new-password"
                            />
                          </WLANFormPSKsPassword>
                          <WLANFormPSKVlanTitle>
                            VLAN ID for Savant Remote
                            <br />
                            (Optional)
                          </WLANFormPSKVlanTitle>
                          <TextField
                            name="savant_vlanid_remote"
                            placeholder="VLAN ID"
                            autoComplete="new-password"
                          />
                        </WLANFormPSKsInputs>
                      )}
                    </WLANFormBlock>
                    <WLANFormBlock>
                      <CheckboxField
                        name="savant_passPhrase_homeowner_enabled"
                        labelText="Passphrase for Homeowner"
                      />
                      {formikHelpers.values
                        .savant_passPhrase_homeowner_enabled && (
                        <WLANFormPSKsInputs>
                          <WLANFormPSKsPassword>
                            <PasswordField
                              name="savant_passPhrase_homeowner"
                              placeholder="Pass-Phrase"
                              inputId={`${wlanName}_homeowner_password`}
                              autoComplete="new-password"
                            />
                          </WLANFormPSKsPassword>
                          <WLANFormPSKVlanTitle>
                            VLAN ID for Homeowner
                            <br />
                            (Optional)
                          </WLANFormPSKVlanTitle>
                          <TextField
                            name="savant_vlanid_homeowner"
                            placeholder="VLAN ID"
                            autoComplete="new-password"
                          />
                        </WLANFormPSKsInputs>
                      )}
                    </WLANFormBlock>
                    <WLANFormBlock>
                      <CheckboxField
                        name="savant_passPhrase_custom_enabled"
                        labelText="Custom Passphrase"
                      />
                      {formikHelpers.values
                        .savant_passPhrase_custom_enabled && (
                        <WLANFormPSKsInputs>
                          <WLANFormPSKsPassword>
                            <PasswordField
                              name="savant_passPhrase_custom"
                              placeholder="Pass-Phrase"
                              inputId={`${wlanName}_custom_password`}
                              autoComplete="new-password"
                            />
                          </WLANFormPSKsPassword>
                          <WLANFormPSKVlanTitle>
                            VLAN ID for Custom
                            <br />
                            (Optional)
                          </WLANFormPSKVlanTitle>
                          <TextField
                            name="savant_vlanid_custom"
                            placeholder="VLAN ID"
                            autoComplete="new-password"
                          />
                        </WLANFormPSKsInputs>
                      )}
                    </WLANFormBlock>
                  </>
                )}
                <WLANFormSeparator />
                <WLANFormBlock>
                  <ToggleField
                    name="portal_enabled"
                    label="Enable guest portal"
                  />

                  {formikHelpers.values.portal_enabled && (
                    <WLANFormBlockSubgroup>
                      <WLANFormSubTitle>
                        Guest Portal Configuration
                      </WLANFormSubTitle>

                      {false && (
                        <WLANFormBlock>
                          <WLANFormBlock>
                            Require following fields from user
                          </WLANFormBlock>

                          <WLANFormBlock>
                            <CheckboxField
                              name="guest_fullname"
                              labelText="Full Name"
                            />
                          </WLANFormBlock>

                          <WLANFormBlock>
                            <CheckboxField
                              name="guest_email"
                              labelText="Email Address"
                            />
                          </WLANFormBlock>

                          <WLANFormSeparator />

                          <WLANFormBlock>
                            Require following fields
                          </WLANFormBlock>
                        </WLANFormBlock>
                      )}

                      <WLANFormBlock>Authorization Options</WLANFormBlock>
                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_passphrase_enabled"
                          labelText="Create Passphrase"
                        />
                      </WLANFormBlock>

                      {formikHelpers.values.portal_passphrase_enabled && (
                        <WLANFormBlock>
                          <PasswordField
                            name="portal_password"
                            placeholder="Guest Pass-Phrase"
                            showLabel
                            inputId={`${wlanName}_guest_password`}
                            autoComplete="new-password"
                          />
                        </WLANFormBlock>
                      )}

                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_email_enabled"
                          labelText="Authentication code via Email"
                        />
                      </WLANFormBlock>
                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_sms_enabled"
                          labelText="Authentication code via Text Message"
                        />
                      </WLANFormBlock>
                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_google_enabled"
                          labelText="Google Sign In"
                        />
                      </WLANFormBlock>
                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_facebook_enabled"
                          labelText="Facebook Sign In"
                        />
                      </WLANFormBlock>
                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_amazon_enabled"
                          labelText="Amazon Sign In"
                        />
                      </WLANFormBlock>
                      <WLANFormBlock>
                        <CheckboxField
                          name="portal_microsoft_enabled"
                          labelText="Microsoft Sign In"
                        />
                      </WLANFormBlock>
                      <WLANFormBlock>
                        <WLANFormGuestTimeInputs>
                          Devices remain authorized for
                          <WLANFormGuestTimeAmountInputs>
                            <TextField
                              name="portal_expire"
                              placeholder=""
                              inputId={`${wlanName}_auth_timer`}
                              autoComplete="new-password"
                            />
                          </WLANFormGuestTimeAmountInputs>
                          <WLANFormGuestTimeAmountDropdown>
                            <Dropdown
                              options={GuestRangeOptions}
                              name="savant_expire_range"
                              value={guestValueDuration}
                              placeholder=""
                              onChange={(option: DropdownOption) =>
                                setGuestDuration(Number(option.value))
                              }
                            />
                          </WLANFormGuestTimeAmountDropdown>
                        </WLANFormGuestTimeInputs>
                      </WLANFormBlock>
                    </WLANFormBlockSubgroup>
                  )}
                </WLANFormBlock>
                <WLANFormSeparator />

                <WLANFormBlock>
                  <WLANFormSubTitle>Radio Band</WLANFormSubTitle>
                  <CheckboxField
                    name="bands_24"
                    labelText="2.4 GHz"
                    value="24"
                    inputId={`${wlanName}_band24`}
                    showLabel
                  />
                  <WLANFormSpacer>&nbsp;&nbsp;</WLANFormSpacer>
                  <CheckboxField
                    name="bands_5"
                    labelText="5 GHz"
                    value="5"
                    inputId={`${wlanName}_band5`}
                    showLabel
                  />
                  <WLANFormSpacer>&nbsp;&nbsp;</WLANFormSpacer>
                  <CheckboxField
                    name="bands_6"
                    labelText="6 GHz"
                    value="6"
                    inputId={`${wlanName}_band6`}
                    showLabel
                  />
                </WLANFormBlock>
                <WLANFormSeparator />
                <WLANFormBlock>
                  <WLANFormSubTitle>Fast Roaming</WLANFormSubTitle>
                  <RadioField
                    name="roam_mode"
                    label="Legacy/Default"
                    value="NONE"
                    inputId={`${wlanName}_roam_legacy`}
                  />
                  <RadioField
                    name="roam_mode"
                    label="Allow .11r"
                    value="11r"
                    inputId={`${wlanName}_roam_11r`}
                  />
                </WLANFormBlock>
                <WLANFormSeparator />
                <WLANFormBlock>
                  <WLANFormSubTitle>Data Rates</WLANFormSubTitle>
                  <CheckboxField
                    name="rateSet"
                    labelText="No Legacy 2.4G (no 802.11b)"
                  />
                </WLANFormBlock>

                <WLANFormSeparator />
                <WLANFormBlock>
                  <WLANFormSubTitle>VLAN</WLANFormSubTitle>
                  <TextField
                    name="vlan_id"
                    placeholder="Savant VLAN"
                    showLabel
                    inputId={`${wlanName}_vlan_id`}
                    autoComplete="new-password"
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#A5A5A5',
                      marginLeft: '5px',
                      fontSize: '12px',
                      marginTop: '-25px',
                    }}
                  >
                    VLAN IDs must be comma separated numeric values or
                    non-overlapping ranges(eg: 100-200) between 1 and 4094 or
                    variable enclosed in &#123;&#123;*&#125;&#125;
                  </div>
                </WLANFormBlock>
                <WLANFormSeparator />
                <WLANFormBlock>
                  <CheckboxField
                    name="disable_11ax"
                    labelText="Wi-Fi 6 Protocols"
                  />
                </WLANFormBlock>
                <WLANFormSeparator />
                <WLANFormBlock>
                  <Checkbox
                    name="bonjour_enabled"
                    checked={enabledBonjour}
                    onChange={(e) => setEnabledBonjour(e.target.checked)}
                    labelText="Enable Bonjour Gateway"
                  />

                  {enabledBonjourSelect && (
                    <>
                      <RadioField
                        checked={enabledBonjourSelect}
                        onChange={(e: ChangeEvent<any>) =>
                          setEnabledBonjourSelect(!e.target.checked)
                        }
                        name="savant_bonjour_services_all"
                        label="All services"
                        value={true}
                        inputId={`${wlanName}_bonjour_all`}
                      />
                      <RadioField
                        checked={!enabledBonjourSelect}
                        onChange={(e: ChangeEvent<any>) =>
                          setEnabledBonjourSelect(e.target.checked)
                        }
                        name="savant_bonjour_services_all"
                        label="Specific Services"
                        value={false}
                        inputId={`${wlanName}_bonjour_select`}
                      />
                    </>
                  )}

                  {enabledBonjour && (
                    <WLANFormBlock>
                      <WLANFormBlock>
                        <WLANBonjourWrapperInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="airdrop"
                              labelText="AirDrop"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourAirdrop}
                            />
                          </WLANBonjourInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="nfs"
                              labelText="NFS"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourNFS}
                            />
                          </WLANBonjourInputs>
                        </WLANBonjourWrapperInputs>
                        <WLANBonjourWrapperInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="airplay"
                              labelText="AirPlay"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourAirplay}
                            />
                          </WLANBonjourInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="rsp._tcp.local"
                              labelText="Roku"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourRoku}
                            />
                          </WLANBonjourInputs>
                        </WLANBonjourWrapperInputs>

                        <WLANBonjourWrapperInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="airprint"
                              labelText="AirPrint"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourAirprint}
                            />
                          </WLANBonjourInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="scanner"
                              labelText="Scanner"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourScanner}
                            />
                          </WLANBonjourInputs>
                        </WLANBonjourWrapperInputs>
                        <WLANBonjourWrapperInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="amzn-wplay"
                              labelText="Amazon Devices"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourAmazon}
                            />
                          </WLANBonjourInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="spotify-connect"
                              labelText="Spotify Connect"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourSpotify}
                            />
                          </WLANBonjourInputs>
                        </WLANBonjourWrapperInputs>
                        <WLANBonjourWrapperInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="homekit"
                              labelText="Apple HomeKit"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourHomekit}
                            />
                          </WLANBonjourInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="sleep-proxy"
                              labelText="Wake on Network/Sleep Proxy"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourSleep}
                            />
                          </WLANBonjourInputs>
                        </WLANBonjourWrapperInputs>
                        <WLANBonjourWrapperInputs>
                          <WLANBonjourInputs>
                            <Checkbox
                              name="bonjour_services_items"
                              value="googlecast"
                              labelText="Google Cast"
                              onChange={(e: ChangeEvent<any>) =>
                                handleBonjourChange(e)
                              }
                              checked={enabledBonjourGoogleCast}
                            />
                          </WLANBonjourInputs>
                        </WLANBonjourWrapperInputs>
                      </WLANFormBlock>
                    </WLANFormBlock>
                  )}
                </WLANFormBlock>
                <WLANFormBlock>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#A5A5A5',
                      marginLeft: '25px',
                      fontSize: '12px',
                    }}
                  >
                    Discovery VLANs
                  </div>
                  <TextArea
                    name="bonjour_additional_vlan_ids"
                    placeholder=" "
                    showLabel
                    rows="3"
                    inputId={`${wlanName}_discovery_vlans`}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: '#A5A5A5',
                      marginLeft: '25px',
                      fontSize: '12px',
                      marginTop: '-25px',
                    }}
                  >
                    VLAN IDs must be comma separated numeric values or
                    non-overlapping ranges(eg: 100-200) between 1 and 4094 or
                    variable enclosed in &#123;&#123;*&#125;&#125;
                  </div>
                </WLANFormBlock>
                <div>
                  <CommonButton
                    type="submit"
                    variant="primary"
                    disabled={formikHelpers.isSubmitting}
                  >
                    {wlan.id === undefined ? 'Save WLAN' : 'Update WLAN'}
                  </CommonButton>
                </div>
              </StyledWLANForm>
            )}
          </Formik>
        </>
      )}
    </div>
  )
}

export default CreateWLANForm
